import type {
  AgentCommandExecutor,
  AgentPreviewDelta,
  AgentToolResult,
  IAgentTool,
} from '../../../../core/ai/agent/IAgentTool';
import type { ICommand } from '../../../../core/commands/ICommand';
import type { Node } from '../../../../core/models/scene';
import { useDiagramStore } from '../../../../stores/diagramStore';
import { CreateProcessStepCommand } from '../../commands/CreateProcessStepCommand';
import { CreateDecisionCommand } from '../../commands/CreateDecisionCommand';
import { CreateTerminatorCommand } from '../../commands/CreateTerminatorCommand';
import { CreateSequenceFlowCommand } from '../../commands/CreateSequenceFlowCommand';
import { InsertStepBetweenCommand } from '../../commands/InsertStepBetweenCommand';
import { UpdateFlowchartNodeCommand } from '../../commands/UpdateFlowchartNodeCommand';
import { DeleteEdgeCommand } from '../../../../core/commands/edges/DeleteEdgeCommand';
import { DeleteNodeCommand } from '../../../../core/commands/DeleteNodeCommand';
import { captureProcessBaseline, validateProcessOperations, validateProcessTargetIntent, resolveProcessStep, isProcessStep, ProcessChangeError } from '../services/ProcessChangePreflight';

import { PROCESS_OPERATION_SCHEMA } from '../services/ProcessOperationContract';

/**
 * propose_process_changes — RDM-native change-set editing for process diagrams.
 *
 * Instead of re-emitting the whole diagram (which the model does unreliably for
 * large flows), the agent emits a SMALL change-set — add a step, connect two
 * steps, insert between, rename, remove. Each op references existing steps by
 * stable IDs or unambiguous labels; coordinates remain a canvas concern.
 *
 * Layout stays where it belongs — the canvas owns it. RDM/this tool only
 * describe the structural change; the underlying process commands
 * (InsertStepBetween, CreateProcessStep, …) run the canvas layout. Ops execute
 * sequentially so a step added earlier in the batch can be referenced by a
 * later op.
 */

type Op = Record<string, unknown>;

const KIND_TO_VARIANT: Record<string, string> = {
  decision: 'decision',
  start: 'start-end',
  end: 'start-end',
  task: 'process',
  process: 'process',
};

export const proposeProcessChangesTool: IAgentTool = {
  name: 'propose_process_changes',
  description:
    'Edit the CURRENT process diagram with a small change-set. This is the tool for ANY change to an existing diagram (add/insert/connect/rename/remove steps) — do NOT rebuild the whole diagram with rdm_apply for an edit. Reference existing steps by their stable ID or an unambiguous label; new steps you add can be referenced by label later in the same batch. Layout is automatic.',
  parameters: {
    type: 'object',
    properties: {
      operations: {
        type: 'array',
        description: 'Ordered list of change operations to apply.',
        minItems: 1,
        items: PROCESS_OPERATION_SCHEMA,
      },
    },
    required: ['operations'],
    additionalProperties: false,
  },
  supportedDiagramTypes: ['flowchart'],
  tier: 'structural',
  category: 'edit',

  async execute(params, context): Promise<AgentToolResult> {
    if (!context.canWrite) {
      return { success: false, summary: 'You do not have edit access to this diagram.' };
    }
    const operations = Array.isArray(params.operations) ? (params.operations as Op[]) : [];
    if (operations.length === 0) {
      return { success: false, summary: 'No operations provided.' };
    }

    try {
      validateProcessTargetIntent(operations, context.diagramData.nodes, context);
      validateProcessOperations(operations, context.diagramData.nodes, context.diagramData.edges);
    }
    catch (error) {
      if (!(error instanceof ProcessChangeError)) throw error;
      return { success: false, summary: error.message, data: { code: error.code, needsInput: true } };
    }
    const assertBaseline = captureProcessBaseline();
    const execute = async (executor: AgentCommandExecutor): Promise<AgentToolResult> => {
      try {
        assertBaseline();
        return summarizeRunResult(await runOps(operations, executor));
      } catch (error) {
        if (!(error instanceof ProcessChangeError)) throw error;
        return { success: false, summary: error.message, data: { code: error.code, needsInput: true } };
      }
    };

    // Small change-sets apply immediately through undoable commands. Larger
    // restructures are staged as a preview the user confirms first — mirrors
    // the network domain's auto-apply-single / preview-compose split.
    if (operations.length > AUTO_APPLY_MAX_OPS) {
      const { delta, description } = planOps(operations);
      return {
        success: true,
        summary: `Proposed ${operations.length} changes — review and apply: ${description}`,
        preview: {
          description,
          delta,
          confirm: {
            kind: 'command-sequence',
            run: execute,
          },
        },
      };
    }

    return {
      success: true,
      summary: `Applying ${operations.length} process change${operations.length === 1 ? '' : 's'}...`,
      execution: {
        kind: 'command-sequence',
        run: execute,
      },
    };
  },
};

/** Change-sets larger than this are staged as a preview the user confirms. */
const AUTO_APPLY_MAX_OPS = 2;

interface RunResult {
  applied: string[];
  failures: string[];
  createdIds: string[];
}

async function runOps(
  operations: Op[],
  executor: AgentCommandExecutor,
): Promise<RunResult> {
  // Label → id for steps created during this batch, so later ops can target them.
  const createdByLabel = new Map<string, string>();
  const applied: string[] = [];
  const failures: string[] = [];
  const createdIds: string[] = [];

  for (const raw of operations) {
    const op = String(raw.op ?? '').trim();
    try {
      const result = await applyOp(op, raw, executor, createdByLabel);
      if (result.ok) {
        applied.push(result.summary);
        if (result.createdId) createdIds.push(result.createdId);
      } else {
        failures.push(result.summary);
      }
    } catch (error) {
      failures.push(`${op}: ${error instanceof Error ? error.message : String(error)}`);
    }
  }
  return { applied, failures, createdIds };
}

function summarizeRunResult(result: RunResult): AgentToolResult {
  const { applied, failures, createdIds } = result;
  if (applied.length === 0) {
    return { success: false, summary: `Could not apply the changes. ${failures.join(' ')}`.trim() };
  }
  const summary =
    `Applied ${applied.length} change${applied.length === 1 ? '' : 's'}: ${applied.join('; ')}.` +
    (failures.length > 0 ? ` Skipped: ${failures.join('; ')}.` : '');
  return { success: true, summary, createdResourceIds: createdIds.length ? createdIds : undefined };
}

/**
 * Best-effort, side-effect-free plan of a change-set for the preview card:
 * node-level additions/removals/updates + a human description. Connect/
 * disconnect have no node delta, so they only show in the description.
 */
function planOps(operations: Op[]): { delta: AgentPreviewDelta; description: string } {
  const delta: AgentPreviewDelta = { additions: [], removals: [], moves: [], updates: [] };
  const lines: string[] = [];
  let previewNodes = liveSteps().map(node => ({ ...node, attrs: { ...node.attrs } }));

  operations.forEach((raw, i) => {
    const op = String(raw.op ?? '').trim();
    const label = strOrUndef(raw.label);
    const kind = strOrUndef(raw.stepKind) ?? 'task';
    const type = kind === 'decision' ? 'decision' : kind === 'start' || kind === 'end' ? 'terminator' : 'process';
    switch (op) {
      case 'insert_step':
        if (label) { delta.additions.push({ id: `new-${i}`, type, name: label }); previewNodes.push({ id: `new-${i}`, type, x: 0, y: 0, attrs: { label } }); }
        lines.push(`insert "${label ?? '?'}" between "${raw.from ?? '?'}" and "${raw.to ?? '?'}"`);
        break;
      case 'add_step':
        if (label) { delta.additions.push({ id: `new-${i}`, type, name: label }); previewNodes.push({ id: `new-${i}`, type, x: 0, y: 0, attrs: { label } }); }
        lines.push(raw.after ? `add "${label ?? '?'}" after "${raw.after}"` : `add "${label ?? '?'}"`);
        break;
      case 'rename': {
        const target = resolveProcessStep(previewNodes, String(raw.target));
        const addition = delta.additions.find(node => node.id === target.id);
        if (addition) addition.name = label;
        else delta.updates.push({ nodeId: target.id, change: { field: 'label', oldValue: nodeLabel(target), newValue: label } });
        target.attrs = { ...target.attrs, label };
        lines.push(`rename "${raw.target ?? '?'}" → "${label ?? '?'}"`);
        break;
      }
      case 'remove_step': {
        const target = resolveProcessStep(previewNodes, String(raw.target));
        if (delta.additions.some(node => node.id === target.id)) delta.additions = delta.additions.filter(node => node.id !== target.id);
        else delta.removals.push({ nodeId: target.id, originalData: { name: nodeLabel(target) } });
        previewNodes = previewNodes.filter(node => node.id !== target.id);
        lines.push(`remove "${raw.target ?? '?'}"`);
        break;
      }
      case 'connect':
        lines.push(`connect "${raw.from ?? '?'}" → "${raw.to ?? '?'}"`);
        break;
      case 'disconnect':
        lines.push(`disconnect "${raw.from ?? '?'}" → "${raw.to ?? '?'}"`);
        break;
      default:
        lines.push(op);
    }
  });

  return { delta, description: lines.join('; ') };
}

interface OpResult {
  ok: boolean;
  summary: string;
  createdId?: string;
}

async function applyOp(
  op: string,
  raw: Op,
  executor: AgentCommandExecutor,
  createdByLabel: Map<string, string>,
): Promise<OpResult> {
  const label = strOrUndef(raw.label);
  const lane = strOrUndef(raw.lane);
  const kind = strOrUndef(raw.stepKind) ?? 'task';

  switch (op) {
    case 'insert_step': {
      const from = resolveStep(strOrUndef(raw.from), createdByLabel);
      const to = resolveStep(strOrUndef(raw.to), createdByLabel);
      if (!label) return fail('insert_step needs a label');
      if (!from) return fail(`insert_step: could not find step "${raw.from}"`);
      if (!to) return fail(`insert_step: could not find step "${raw.to}"`);
      const edge = findEdge(from.id, to.id);
      if (!edge) return fail(`insert_step: no flow from "${from.label}" to "${to.label}"`);
      const mid = midpoint(from, to);
      const cmd = new InsertStepBetweenCommand({
        edgeId: edge.id,
        variant: KIND_TO_VARIANT[kind] ?? 'process',
        midpointX: mid.x,
        midpointY: mid.y,
        label,
        lane,
      });
      await executor.execute(cmd);
      const createdId = cmd.getCreatedShapeId();
      if (createdId) {
        createdByLabel.set(norm(label), createdId);
        if (kind === 'start' || kind === 'end') await executor.execute(new UpdateFlowchartNodeCommand({ nodeId: createdId, changes: { attrs: { stepKind: kind } } }));
      }
      return ok(`inserted "${label}" between "${from.label}" and "${to.label}"`, createdId);
    }

    case 'add_step': {
      if (!label) return fail('add_step needs a label');
      const after = strOrUndef(raw.after) ? resolveStep(strOrUndef(raw.after), createdByLabel) : null;
      if (raw.after && !after) return fail(`add_step: could not find step "${raw.after}"`);
      const pos = after
        ? { x: (after.x ?? 0) + nodeWidth(after) + 80, y: after.y ?? 0 }
        : defaultDropPosition();
      const createCmd = buildCreateCommand(kind, label, pos);
      await executor.execute(createCmd);
      const createdId = createCmd.getCreatedId();
      if (!createdId) return fail(`add_step: failed to create "${label}"`);
      createdByLabel.set(norm(label), createdId);
      if (lane) {
        await executor.execute(new UpdateFlowchartNodeCommand({ nodeId: createdId, changes: { attrs: { lane } } }));
      }
      if (after) {
        await executor.execute(new CreateSequenceFlowCommand({ from: after.id, to: createdId }));
        return ok(`added "${label}" after "${after.label}"`, createdId);
      }
      return ok(`added "${label}"`, createdId);
    }

    case 'connect': {
      const from = resolveStep(strOrUndef(raw.from), createdByLabel);
      const to = resolveStep(strOrUndef(raw.to), createdByLabel);
      if (!from) return fail(`connect: could not find step "${raw.from}"`);
      if (!to) return fail(`connect: could not find step "${raw.to}"`);
      if (findEdge(from.id, to.id)) return fail(`connect: "${from.label}" already flows to "${to.label}"`);
      await executor.execute(new CreateSequenceFlowCommand({ from: from.id, to: to.id }));
      return ok(`connected "${from.label}" → "${to.label}"`);
    }

    case 'disconnect': {
      const from = resolveStep(strOrUndef(raw.from), createdByLabel);
      const to = resolveStep(strOrUndef(raw.to), createdByLabel);
      if (!from || !to) return fail(`disconnect: could not find both steps`);
      const edge = findEdge(from.id, to.id);
      if (!edge) return fail(`disconnect: no flow from "${from.label}" to "${to.label}"`);
      await executor.execute(new DeleteEdgeCommand(edge.id));
      return ok(`disconnected "${from.label}" → "${to.label}"`);
    }

    case 'rename': {
      const target = resolveStep(strOrUndef(raw.target), createdByLabel);
      if (!target) return fail(`rename: could not find step "${raw.target}"`);
      if (!label) return fail('rename needs a new label');
      await executor.execute(new UpdateFlowchartNodeCommand({ nodeId: target.id, changes: { attrs: { label } } }));
      return ok(`renamed "${target.label}" → "${label}"`);
    }

    case 'remove_step': {
      const target = resolveStep(strOrUndef(raw.target), createdByLabel);
      if (!target) return fail(`remove_step: could not find step "${raw.target}"`);
      await executor.execute(new DeleteNodeCommand([target.id]));
      return ok(`removed "${target.label}"`);
    }

    default:
      return fail(`unknown op "${op}"`);
  }
}

// ── helpers ────────────────────────────────────────────────────────────────

interface ResolvedStep {
  id: string;
  label: string;
  x?: number;
  y?: number;
  node: Node;
}



function nodeLabel(n: Node): string {
  return String((n.attrs?.label ?? n.attrs?.name ?? n.props?.label ?? n.name ?? '') as string);
}

function liveSteps(): Node[] {
  return useDiagramStore.getState().nodes.filter(isProcessStep);
}

function resolveStep(query: string | undefined, createdByLabel: Map<string, string>): ResolvedStep | null {
  if (!query) return null;
  const q = norm(query);
  const nodes = useDiagramStore.getState().nodes;

  // 1. A step we created earlier in this same batch.
  const createdId = createdByLabel.get(q);
  if (createdId) {
    const node = nodes.find((n) => n.id === createdId);
    if (node) return toResolved(node);
  }
  return toResolved(resolveProcessStep(liveSteps(), query));
}

function toResolved(node: Node): ResolvedStep {
  return { id: node.id, label: nodeLabel(node) || node.id, x: node.x, y: node.y, node };
}

function findEdge(fromId: string, toId: string) {
  return useDiagramStore.getState().edges.find((e) => e.from === fromId && e.to === toId);
}

function nodeWidth(s: ResolvedStep): number {
  return (s.node.w ?? s.node.width ?? 120) as number;
}

function midpoint(a: ResolvedStep, b: ResolvedStep): { x: number; y: number } {
  const aw = nodeWidth(a);
  const ah = (a.node.h ?? a.node.height ?? 80) as number;
  const bw = nodeWidth(b);
  const bh = (b.node.h ?? b.node.height ?? 80) as number;
  const ax = (a.x ?? 0) + aw / 2;
  const ay = (a.y ?? 0) + ah / 2;
  const bx = (b.x ?? 0) + bw / 2;
  const by = (b.y ?? 0) + bh / 2;
  return { x: (ax + bx) / 2, y: (ay + by) / 2 };
}

function defaultDropPosition(): { x: number; y: number } {
  const nodes = useDiagramStore.getState().nodes;
  if (nodes.length === 0) return { x: 200, y: 200 };
  let maxX = 0;
  let maxY = 0;
  for (const n of nodes) {
    maxX = Math.max(maxX, (n.x ?? 0) + ((n.width ?? n.w ?? 0) as number));
    maxY = Math.max(maxY, (n.y ?? 0) + ((n.height ?? n.h ?? 0) as number));
  }
  return { x: Math.min(maxX + 80, 4000), y: maxY };
}

type CreateStepCommand = ICommand & { getCreatedId(): string | undefined };

function buildCreateCommand(kind: string, label: string, pos: { x: number; y: number }): CreateStepCommand {
  if (kind === 'decision') return new CreateDecisionCommand({ x: pos.x, y: pos.y, label });
  if (kind === 'start') return new CreateTerminatorCommand({ x: pos.x, y: pos.y, label, terminatorType: 'start' });
  if (kind === 'end') return new CreateTerminatorCommand({ x: pos.x, y: pos.y, label, terminatorType: 'end' });
  return new CreateProcessStepCommand({ x: pos.x, y: pos.y, label });
}

const norm = (v: string) => v.trim().toLowerCase();
const strOrUndef = (v: unknown): string | undefined => {
  const s = typeof v === 'string' ? v.trim() : '';
  return s.length > 0 ? s : undefined;
};
const ok = (summary: string, createdId?: string): OpResult => ({ ok: true, summary, createdId });
const fail = (summary: string): OpResult => ({ ok: false, summary });
